import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Buildings, Plus, Power, Trash, PencilSimple, SignOut,
  Users, UsersThree, Receipt, ShieldStar,
  EnvelopeSimple, Phone, ChatCircleDots, ArrowSquareOut, PaperPlaneTilt,
} from '@phosphor-icons/react';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  suspended: 'bg-amber-100 text-amber-700 border-amber-200',
};

const PLAN_STYLES = {
  basic: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  pro:   'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
};

export default function SuperAdminPage() {
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // Platform-level state — plan catalog (from `/api/plans`) and the
  // singleton platform-settings doc (default trial days). Both are lazy-
  // loaded once on mount and cached in-page since they're rarely edited.
  const [plans, setPlans] = useState([]);
  const [platformSettings, setPlatformSettings] = useState({ default_trial_days: 15 });
  const [trialDaysDraft, setTrialDaysDraft] = useState('15');
  const [savingTrialDefault, setSavingTrialDefault] = useState(false);
  const [supportPhoneDraft, setSupportPhoneDraft] = useState('');
  const [savingSupportPhone, setSavingSupportPhone] = useState(false);
  // Plan editor state — `null` when closed; `{}` to open a "create new"
  // dialog; a fully-hydrated plan object to open an "edit existing" dialog.
  const [planEditor, setPlanEditor] = useState(null);
  const [planDeleteTarget, setPlanDeleteTarget] = useState(null);
  // Leads triage — populated from /api/marketing/leads. Loaded alongside
  // companies so the SuperAdmin sees a single unified inbox.
  const [leads, setLeads] = useState([]);
  const [leadCounts, setLeadCounts] = useState({ new: 0, contacted: 0, qualified: 0, closed: 0 });
  const [leadFilter, setLeadFilter] = useState('all');
  const [leadDetail, setLeadDetail] = useState(null);
  const [leadDeleteTarget, setLeadDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, s, l] = await Promise.all([
        api.get('/companies'),
        api.get('/plans').catch(() => ({ data: [] })),
        api.get('/platform-settings').catch(() => ({ data: { default_trial_days: 15 } })),
        api.get('/marketing/leads').catch(() => ({ data: { leads: [], counts: {} } })),
      ]);
      setCompanies(c.data);
      setPlans(p.data || []);
      setPlatformSettings(s.data || { default_trial_days: 15 });
      setTrialDaysDraft(String((s.data && s.data.default_trial_days) ?? 15));
      setSupportPhoneDraft((s.data && s.data.support_phone) || '');
      setLeads(l.data?.leads || []);
      setLeadCounts({ new: 0, contacted: 0, qualified: 0, closed: 0, ...(l.data?.counts || {}) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSupportPhone = async () => {
    const v = supportPhoneDraft.trim();
    if (!v) { toast.error('Enter a customer care number'); return; }
    setSavingSupportPhone(true);
    try {
      const { data } = await api.put('/platform-settings', { support_phone: v });
      setPlatformSettings(data);
      setSupportPhoneDraft(data.support_phone || '');
      toast.success('Customer care number updated');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingSupportPhone(false);
    }
  };

  const saveTrialDefault = async () => {
    const n = parseInt(trialDaysDraft, 10);
    if (Number.isNaN(n) || n < 0 || n > 365) {
      toast.error('Enter a value between 0 and 365');
      return;
    }
    setSavingTrialDefault(true);
    try {
      const { data } = await api.put('/platform-settings', { default_trial_days: n });
      setPlatformSettings(data);
      setTrialDaysDraft(String(data.default_trial_days));
      toast.success(`Default trial days set to ${data.default_trial_days}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingTrialDefault(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async (company) => {
    const nextStatus = company.status === 'active' ? 'suspended' : 'active';
    try {
      await api.put(`/companies/${company.id}`, { status: nextStatus });
      toast.success(`${company.name} ${nextStatus === 'active' ? 'reactivated' : 'suspended'}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const updateLeadStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`/marketing/leads/${lead.id}`, { status });
      setLeads(ls => ls.map(l => (l.id === lead.id ? data : l)));
      setLeadCounts(prev => {
        const next = { ...prev };
        const from = lead.status || 'new';
        next[from] = Math.max(0, (next[from] || 0) - 1);
        next[status] = (next[status] || 0) + 1;
        return next;
      });
      if (leadDetail?.id === lead.id) setLeadDetail(data);
      toast.success(`Lead marked ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteLead = async (lead) => {
    try {
      await api.delete(`/marketing/leads/${lead.id}`);
      setLeads(ls => ls.filter(l => l.id !== lead.id));
      setLeadCounts(prev => ({ ...prev, [lead.status || 'new']: Math.max(0, (prev[lead.status || 'new'] || 0) - 1) }));
      if (leadDetail?.id === lead.id) setLeadDetail(null);
      setLeadDeleteTarget(null);
      toast.success('Lead removed');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const visibleLeads = leadFilter === 'all' ? leads : leads.filter(l => (l.status || 'new') === leadFilter);

  const totalStats = companies.reduce(
    (acc, c) => {
      acc.active += c.status === 'active' ? 1 : 0;
      acc.suspended += c.status === 'suspended' ? 1 : 0;
      acc.users += c.stats?.users || 0;
      acc.customers += c.stats?.customers || 0;
      acc.invoices += c.stats?.invoices || 0;
      return acc;
    },
    { active: 0, suspended: 0, users: 0, customers: 0, invoices: 0 }
  );

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-zinc-200" data-testid="superadmin-page">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#0B0F1A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center">
              <ShieldStar size={22} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-fuchsia-300/80">Super Admin Console</div>
              <div className="text-sm text-white font-semibold">Tenant Management</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs text-zinc-400">Signed in as</div>
              <div className="text-sm text-white">{user?.email}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-white/20 text-zinc-200 hover:bg-white/10 bg-transparent"
              onClick={logout}
              data-testid="superadmin-logout-btn"
            >
              <SignOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Stats strip */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="superadmin-stats">
          <StatCard label="Total Companies" value={companies.length} icon={<Buildings size={20} weight="duotone" />} accent="from-indigo-500 to-blue-500" />
          <StatCard label="Active" value={totalStats.active} icon={<Power size={20} weight="duotone" />} accent="from-emerald-500 to-teal-500" />
          <StatCard label="Suspended" value={totalStats.suspended} icon={<Power size={20} weight="duotone" />} accent="from-amber-500 to-orange-500" />
          <StatCard label="Tenant Users" value={totalStats.users} icon={<Users size={20} weight="duotone" />} accent="from-fuchsia-500 to-pink-500" />
          <StatCard label="Total Invoices" value={totalStats.invoices} icon={<Receipt size={20} weight="duotone" />} accent="from-cyan-500 to-sky-500" />
        </section>

        {/* Leads inbox — Contact + Demo submissions triage */}
        <section data-testid="leads-section">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-2xl font-semibold text-white tracking-tight">Leads Inbox</h2>
              <p className="text-sm text-zinc-400 mt-1">
                Contact + Demo submissions from the marketing site. Also emailed to
                <code className="ml-1 text-fuchsia-300">smsuitepro@gmail.com</code>.
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {['all', 'new', 'contacted', 'qualified', 'closed'].map(s => {
                const count = s === 'all' ? leads.length : (leadCounts[s] || 0);
                const isActive = leadFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setLeadFilter(s)}
                    data-testid={`leads-filter-${s}`}
                    className={`px-3 py-1.5 rounded-full uppercase tracking-wider font-semibold border transition-colors ${isActive ? 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/40' : 'bg-white/5 text-zinc-400 border-white/10 hover:text-white'}`}
                  >
                    {s} <span className="ml-1 opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Card className="border-white/10 bg-[#141826] text-zinc-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Type</TableHead>
                    <TableHead className="text-zinc-400">Contact</TableHead>
                    <TableHead className="text-zinc-400">Business</TableHead>
                    <TableHead className="text-zinc-400">Message</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400">Received</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-10">Loading leads…</TableCell></TableRow>
                  )}
                  {!loading && visibleLeads.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-10">No leads {leadFilter !== 'all' ? `in "${leadFilter}"` : 'yet'}.</TableCell></TableRow>
                  )}
                  {visibleLeads.map((l) => (
                    <TableRow key={l.id} className="border-white/5 hover:bg-white/[0.03]" data-testid={`lead-row-${l.id}`}>
                      <TableCell>
                        <Badge className={`${l.type === 'demo' ? 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30' : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30'} font-medium uppercase tracking-wide text-[10px] border`}>
                          {l.type === 'demo' ? 'Demo' : 'Contact'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-white">{l.name || '—'}</div>
                        <div className="flex items-center gap-1 text-xs text-zinc-400">
                          <EnvelopeSimple size={12} weight="duotone" /> {l.email || '—'}
                        </div>
                        {l.mobile && (
                          <div className="flex items-center gap-1 text-xs text-zinc-500">
                            <Phone size={12} weight="duotone" /> {l.mobile}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-300 text-sm">
                        {l.business_name || <span className="text-zinc-500">—</span>}
                        {l.business_type && <div className="text-[11px] text-zinc-500 mt-0.5">{l.business_type}</div>}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <div className="text-xs text-zinc-300 line-clamp-2">{l.message || <span className="text-zinc-500">—</span>}</div>
                        {l.preferred_date && <div className="text-[10px] text-zinc-500 mt-0.5">Preferred: {l.preferred_date}</div>}
                      </TableCell>
                      <TableCell>
                        <LeadStatusPill status={l.status || 'new'} />
                      </TableCell>
                      <TableCell className="text-xs text-zinc-400">
                        {l.created_at ? new Date(l.created_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <IconButton
                            title="View / triage"
                            onClick={() => setLeadDetail(l)}
                            testid={`lead-open-${l.id}`}
                          ><ArrowSquareOut size={15} /></IconButton>
                          {(l.status || 'new') !== 'closed' && (
                            <IconButton
                              title="Mark closed"
                              onClick={() => updateLeadStatus(l, 'closed')}
                              testid={`lead-close-${l.id}`}
                              tone="emerald"
                            ><ChatCircleDots size={15} /></IconButton>
                          )}
                          <IconButton
                            title="Delete"
                            onClick={() => setLeadDeleteTarget(l)}
                            testid={`lead-delete-${l.id}`}
                            tone="red"
                          ><Trash size={15} /></IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>

        {/* Companies section */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-white tracking-tight">Companies</h1>
              <p className="text-sm text-zinc-400 mt-1">Create, suspend, or delete tenant accounts.</p>
            </div>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white hover:opacity-90"
              data-testid="superadmin-create-company-btn"
            >
              <Plus size={16} className="mr-2" /> New Company
            </Button>
          </div>

          <Card className="border-white/10 bg-[#141826] text-zinc-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-zinc-400">Company</TableHead>
                    <TableHead className="text-zinc-400">Plan</TableHead>
                    <TableHead className="text-zinc-400">Trial</TableHead>
                    <TableHead className="text-zinc-400">Plan Expiry</TableHead>
                    <TableHead className="text-zinc-400 text-right">Users</TableHead>
                    <TableHead className="text-zinc-400 text-right">Invoices</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-10">Loading companies…</TableCell></TableRow>
                  )}
                  {!loading && companies.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-zinc-500 py-10">No companies yet — create the first one.</TableCell></TableRow>
                  )}
                  {companies.map((c) => (
                    <TableRow
                      key={c.id}
                      className="border-white/5 hover:bg-white/[0.03]"
                      data-testid={`company-row-${c.id}`}
                    >
                      <TableCell>
                        <div className="font-medium text-white">{c.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{c.id}</div>
                        {c.owner_email && <div className="text-xs text-zinc-500 mt-0.5">{c.owner_email}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${PLAN_STYLES[c.subscription_plan] || 'bg-zinc-100 text-zinc-700'} font-medium border uppercase tracking-wide text-[10px]`}>
                          {c.subscription_plan}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {c.trial_ends_at ? (
                          <div className={c.trial_expired ? 'text-red-400' : (c.trial_remaining_days <= 3 ? 'text-amber-400' : 'text-zinc-300')}>
                            <div className="text-sm font-medium">
                              {c.trial_expired
                                ? 'Expired'
                                : `${c.trial_remaining_days} day${c.trial_remaining_days === 1 ? '' : 's'}`}
                            </div>
                            <div className="text-[10px] text-zinc-500">
                              ends {new Date(c.trial_ends_at).toLocaleDateString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-500">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.plan_expires_at ? (
                          <div className={c.plan_expired ? 'text-red-400' : (c.plan_remaining_days <= 7 ? 'text-amber-400' : 'text-zinc-300')}>
                            <div className="text-sm font-medium capitalize">
                              {c.plan_period || 'subscription'}
                            </div>
                            <div className="text-[10px] text-zinc-500">
                              {c.plan_expired
                                ? `expired ${new Date(c.plan_expires_at).toLocaleDateString()}`
                                : `${c.plan_remaining_days} day${c.plan_remaining_days === 1 ? '' : 's'} · ${new Date(c.plan_expires_at).toLocaleDateString()}`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-zinc-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-zinc-200">{c.stats?.users ?? 0}</TableCell>
                      <TableCell className="text-right text-zinc-200">{c.stats?.invoices ?? 0}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_STYLES[c.status] || 'bg-zinc-100 text-zinc-700'} font-medium border`}>
                          {c.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-1">
                          <IconButton
                            title="Edit / Manage Plan"
                            onClick={() => setEditTarget(c)}
                            testid={`edit-company-${c.id}`}
                          >
                            <PencilSimple size={15} />
                          </IconButton>
                          <IconButton
                            title={c.status === 'active' ? 'Suspend' : 'Activate'}
                            onClick={() => toggleStatus(c)}
                            testid={`toggle-status-${c.id}`}
                            tone={c.status === 'active' ? 'amber' : 'emerald'}
                          >
                            <Power size={15} />
                          </IconButton>
                          <IconButton
                            title="Delete"
                            disabled={c.id === 'default'}
                            onClick={() => setDeleteTarget(c)}
                            testid={`delete-company-${c.id}`}
                            tone="red"
                          >
                            <Trash size={15} />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>

        {/* Platform settings + Plan pricing */}
        <section className="mb-6 space-y-4">
          <Card className="border-white/10 bg-[#141826] text-zinc-200">
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
              <div className="min-w-[220px]">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Platform Settings</div>
                <div className="text-white text-lg font-semibold">Default Trial Days</div>
                <p className="text-xs text-zinc-500 mt-1">
                  Applied to every new tenant unless overridden at creation.
                </p>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={trialDaysDraft}
                  onChange={(e) => setTrialDaysDraft(e.target.value)}
                  className="bg-white/5 border-white/20 text-white w-24 h-9"
                  data-testid="platform-default-trial-days"
                />
                <span className="text-xs text-zinc-500">days</span>
                <Button
                  size="sm"
                  onClick={saveTrialDefault}
                  disabled={savingTrialDefault || String(platformSettings.default_trial_days) === trialDaysDraft}
                  className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white h-9"
                  data-testid="platform-default-trial-save"
                >
                  {savingTrialDefault ? 'Saving…' : 'Save'}
                </Button>
                <span className="text-[11px] text-zinc-500 ml-2">
                  Currently: <b className="text-zinc-300">{platformSettings.default_trial_days}d</b>
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-[#141826] text-zinc-200">
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
              <div className="min-w-[220px]">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Platform Settings</div>
                <div className="text-white text-lg font-semibold">Customer Care Number</div>
                <p className="text-xs text-zinc-500 mt-1">
                  Shown as "Any help? Please contact …" on the login popup, sign-up page, Contact page and site footer.
                </p>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Input
                  type="tel"
                  value={supportPhoneDraft}
                  onChange={(e) => setSupportPhoneDraft(e.target.value)}
                  placeholder="+91 99902 89610"
                  className="bg-white/5 border-white/20 text-white w-48 h-9"
                  data-testid="platform-support-phone"
                />
                <Button
                  size="sm"
                  onClick={saveSupportPhone}
                  disabled={savingSupportPhone || (platformSettings.support_phone || '') === supportPhoneDraft.trim()}
                  className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white h-9"
                  data-testid="platform-support-phone-save"
                >
                  {savingSupportPhone ? 'Saving…' : 'Save'}
                </Button>
                <span className="text-[11px] text-zinc-500 ml-2">
                  Currently: <b className="text-zinc-300" data-testid="platform-support-phone-current">{platformSettings.support_phone || '—'}</b>
                </span>
              </div>
            </CardContent>
          </Card>

          <EmailSettingsCard settings={platformSettings} onSaved={(s) => setPlatformSettings(s)} />

          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Plans & Pricing</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map(p => (
                <Card key={p.id} className="border-white/10 bg-[#141826] text-zinc-200 relative group">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-400">Plan · {p.name}</div>
                      <div className="flex items-center gap-1">
                        <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 text-[10px] uppercase tracking-wide border">
                          {p.id}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => setPlanEditor({ ...p })}
                          title="Edit plan"
                          data-testid={`plan-edit-${p.id}`}
                          className="ml-1 opacity-40 group-hover:opacity-100 transition-opacity text-zinc-300 hover:text-white p-1 rounded"
                        >
                          <PencilSimple size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPlanDeleteTarget(p)}
                          title="Delete plan"
                          data-testid={`plan-delete-${p.id}`}
                          className="opacity-40 group-hover:opacity-100 transition-opacity text-red-300 hover:text-red-200 p-1 rounded"
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-2xl font-semibold text-white">₹{p.price}</span>
                      <span className="text-xs text-zinc-500">/{p.period}</span>
                      {p.mrp && p.mrp !== p.price && (
                        <span className="text-xs text-zinc-500 line-through">MRP ₹{p.mrp}</span>
                      )}
                    </div>
                    <ul className="text-[11px] text-zinc-400 space-y-1 mt-2">
                      {(p.features || []).slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <span className="text-emerald-400">✓</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}

              <button
                type="button"
                onClick={() => setPlanEditor({})}
                data-testid="plan-add-btn"
                className="border-2 border-dashed border-white/10 rounded-lg text-zinc-400 hover:border-fuchsia-500/40 hover:text-fuchsia-300 hover:bg-fuchsia-500/5 transition-colors flex flex-col items-center justify-center py-8 gap-2 min-h-[180px]"
              >
                <Plus size={22} />
                <span className="text-xs uppercase tracking-wider font-semibold">Add Plan</span>
                <span className="text-[10px] text-zinc-500">e.g. Premium, Enterprise</span>
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Create dialog */}
      <CreateCompanyDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        loading={creating}
        setLoading={setCreating}
        onCreated={load}
        defaultTrialDays={platformSettings.default_trial_days}
      />

      {/* Edit dialog */}
      <EditCompanyDialog
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={load}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="superadmin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the company along with <b>all its users, customers, invoices, services, finance records, drafts, and settings</b>.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                const t = deleteTarget;
                try {
                  await api.delete(`/companies/${t.id}`);
                  toast.success(`Deleted ${t.name}`);
                  setDeleteTarget(null);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || err.message);
                }
              }}
              data-testid="superadmin-delete-confirm-btn"
            >
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Plan editor + delete confirm */}
      <PlanEditorDialog
        target={planEditor}
        onClose={() => setPlanEditor(null)}
        onSaved={load}
      />
      <AlertDialog open={!!planDeleteTarget} onOpenChange={(o) => !o && setPlanDeleteTarget(null)}>
        <AlertDialogContent data-testid="plan-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {planDeleteTarget?.name} plan?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes this plan from the catalog and the public marketing page. Companies currently on this plan block the delete — move them off first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                const t = planDeleteTarget;
                try {
                  await api.delete(`/plans/${t.id}`);
                  toast.success(`Deleted ${t.name} plan`);
                  setPlanDeleteTarget(null);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || err.message);
                }
              }}
              data-testid="plan-delete-confirm-btn"
            >
              Delete plan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lead detail + triage dialog */}
      <LeadDetailDialog
        lead={leadDetail}
        onClose={() => setLeadDetail(null)}
        onUpdate={updateLeadStatus}
      />

      {/* Lead delete confirm */}
      <AlertDialog open={!!leadDeleteTarget} onOpenChange={(o) => !o && setLeadDeleteTarget(null)}>
        <AlertDialogContent data-testid="lead-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the submission from
              {' '}<b>{leadDeleteTarget?.name}</b> ({leadDeleteTarget?.email}). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => leadDeleteTarget && deleteLead(leadDeleteTarget)}
              data-testid="lead-delete-confirm-btn"
            >
              Delete lead
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LeadStatusPill({ status }) {
  const styles = {
    new:       'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
    contacted: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    qualified: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
    closed:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  };
  return (
    <Badge className={`${styles[status] || styles.new} font-medium uppercase tracking-wide text-[10px] border`}>
      {status}
    </Badge>
  );
}

function LeadDetailDialog({ lead, onClose, onUpdate }) {
  const open = !!lead;
  if (!lead) return null;
  const statuses = ['new', 'contacted', 'qualified', 'closed'];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lead.type === 'demo' ? 'Demo Request' : 'Contact Submission'}
            <LeadStatusPill status={lead.status || 'new'} />
          </DialogTitle>
          <DialogDescription>
            Received {lead.created_at ? new Date(lead.created_at).toLocaleString() : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <LeadField label="Name" value={lead.name} />
          <LeadField label="Email" value={lead.email} />
          {lead.mobile && <LeadField label="Mobile" value={lead.mobile} />}
          {lead.business_name && <LeadField label="Business" value={lead.business_name} />}
          {lead.business_type && <LeadField label="Business Type" value={lead.business_type} />}
          {lead.preferred_date && <LeadField label="Preferred Date" value={lead.preferred_date} />}
          {lead.message && (
            <div>
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Message</div>
              <div className="rounded-md border bg-[#F8FAFC] text-[#0A0A0A] p-3 text-sm whitespace-pre-wrap">{lead.message}</div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {statuses.filter(s => s !== (lead.status || 'new')).map(s => (
            <Button
              key={s}
              type="button"
              variant="outline"
              onClick={() => onUpdate(lead, s)}
              data-testid={`lead-set-${s}`}
              className="capitalize"
            >Mark {s}</Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadField({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-sm text-[#0A0A0A] font-medium text-right break-all">{value || '—'}</div>
    </div>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <Card className="border-white/10 bg-[#141826]">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center text-white`}>
          {icon}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-400">{label}</div>
          <div className="text-2xl font-semibold text-white">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanEditorDialog({ target, onClose, onSaved }) {
  // `target === null` → dialog closed. `target === {}` → fresh Create.
  // `target` with id → Edit existing.
  const isOpen = !!target;
  const isEdit = !!target?.id;
  const [form, setForm] = useState({
    name: '', mrp: '', price: '', yearly_price: '', period: 'month', features: '', sort_order: '10',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (target) {
      setForm({
        name: target.name || '',
        mrp: target.mrp != null ? String(target.mrp) : '',
        price: target.price != null ? String(target.price) : '',
        yearly_price: target.yearly_price != null ? String(target.yearly_price) : '',
        period: target.period || 'month',
        // Textarea holds one feature per line.
        features: Array.isArray(target.features) ? target.features.join('\n') : '',
        sort_order: target.sort_order != null ? String(target.sort_order) : '10',
      });
      setErr('');
    }
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    const payload = {
      name: form.name.trim(),
      mrp: Number(form.mrp || 0),
      price: Number(form.price || 0),
      yearly_price: form.yearly_price ? Number(form.yearly_price) : null,
      period: form.period,
      features: form.features.split('\n').map(s => s.trim()).filter(Boolean),
      sort_order: Number(form.sort_order || 0),
    };
    if (!payload.name) { setErr('Plan name is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/plans/${target.id}`, payload);
        toast.success(`${payload.name} updated`);
      } else {
        await api.post('/plans', payload);
        toast.success(`${payload.name} added`);
      }
      onClose();
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="plan-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${target?.name || 'Plan'}` : 'Add Plan'}</DialogTitle>
          <DialogDescription>
            Live-published to the marketing page and to every tenant&apos;s Trial Banner.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Plan Name *">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Premium"
              autoFocus
              data-testid="plan-editor-name"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="MRP (₹)">
              <Input
                type="number" min="0"
                value={form.mrp}
                onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                placeholder="2999"
                data-testid="plan-editor-mrp"
              />
            </Field>
            <Field label="Price (₹) *">
              <Input
                type="number" min="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="1499"
                data-testid="plan-editor-price"
              />
            </Field>
            <Field label="Period">
              <select
                value={form.period}
                onChange={(e) => setForm({ ...form, period: e.target.value })}
                className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
                data-testid="plan-editor-period"
              >
                <option value="month">month</option>
                <option value="year">year</option>
              </select>
            </Field>
          </div>
          {/* Yearly price — optional. When set, the marketing page's
              Yearly toggle shows this exact amount + auto-computes savings
              vs 12 × monthly. Blank keeps the toggle disabled for this
              plan (fallback shows monthly × 12 with no discount chip). */}
          <Field label="Yearly Price (₹)">
            <Input
              type="number" min="0"
              value={form.yearly_price}
              onChange={(e) => setForm({ ...form, yearly_price: e.target.value })}
              placeholder={form.price ? `e.g. ${Math.round(Number(form.price) * 10)} (2 months free)` : 'Optional — leave blank to skip yearly'}
              data-testid="plan-editor-yearly-price"
            />
            <p className="text-[10px] text-zinc-500 mt-1 italic">
              Tip: <b>10×monthly</b> gives 2 months free (17% off); <b>10.8×monthly</b> gives 10% off.
            </p>
          </Field>
          <Field label="Features (one per line)">
            <textarea
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              placeholder={'Unlimited users\nMulti-branch\nPriority phone support'}
              rows={5}
              className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm resize-y"
              data-testid="plan-editor-features"
            />
          </Field>
          <Field label="Sort Order">
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              placeholder="10, 20, 30…"
              data-testid="plan-editor-sort"
            />
          </Field>
          {err && <div className="text-sm text-red-500">{err}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="plan-editor-save">
              {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create Plan')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function IconButton({ children, onClick, title, tone = 'default', disabled, testid }) {
  const toneMap = {
    default: 'text-zinc-300 hover:bg-white/10',
    amber:   'text-amber-300 hover:bg-amber-500/10',
    emerald: 'text-emerald-300 hover:bg-emerald-500/10',
    red:     'text-red-300 hover:bg-red-500/10',
  };
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${toneMap[tone]} disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function CreateCompanyDialog({ open, onOpenChange, loading, setLoading, onCreated, defaultTrialDays = 15 }) {
  const [form, setForm] = useState({
    name: '', owner_email: '', phone: '',
    subscription_plan: 'basic', trial_days: defaultTrialDays,
    admin_name: '', admin_email: '', admin_password: '',
    // Paid-plan metadata — only surfaces when the operator picks Pro or
    // manually sets an expiry (e.g. lifetime-basic converted to paid).
    plan_period: 'monthly',
    plan_expires_at: '',
  });
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) {
      setForm({
        name: '', owner_email: '', phone: '',
        subscription_plan: 'basic', trial_days: defaultTrialDays,
        admin_name: '', admin_email: '', admin_password: '',
        plan_period: 'monthly',
        plan_expires_at: '',
      });
      setErr('');
    } else {
      // Rehydrate `trial_days` if the platform-settings default changed
      // while the dialog was closed — keeps SuperAdmin's default in sync.
      setForm(prev => ({ ...prev, trial_days: defaultTrialDays }));
    }
  }, [open, defaultTrialDays]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim()) { setErr('Company name is required'); return; }
    if (form.admin_email && !form.admin_password) { setErr('Set a password for the tenant admin'); return; }
    if (form.admin_password && form.admin_password.length < 8) { setErr('Admin password must be at least 8 characters'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        trial_days: form.subscription_plan === 'basic' ? Number(form.trial_days || 0) : 0,
        // Normalise — only send paid-plan fields when the operator picked Pro
        // or explicitly typed an expiry (avoids polluting basic accounts).
        plan_period: form.plan_expires_at ? form.plan_period : null,
        plan_expires_at: form.plan_expires_at
          ? new Date(form.plan_expires_at + 'T23:59:59').toISOString()
          : null,
        plan_started_at: form.plan_expires_at ? new Date().toISOString() : null,
      };
      await api.post('/companies', payload);
      toast.success(`Created ${form.name}`);
      onOpenChange(false);
      onCreated();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="create-company-dialog">
        <DialogHeader>
          <DialogTitle>Create New Company</DialogTitle>
          <DialogDescription>Provision a fresh tenant on Basic or Pro and optionally bootstrap its first admin.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Section title="Company">
            <Field label="Name *">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Acme CSC"
                data-testid="create-company-name"
                autoFocus
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Owner Email">
                <Input
                  type="email"
                  value={form.owner_email}
                  onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                  placeholder="owner@acme.com"
                  data-testid="create-company-owner-email"
                />
              </Field>
              <Field label="Phone">
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="9999999999"
                  data-testid="create-company-phone"
                />
              </Field>
            </div>
          </Section>

          <Section title="Subscription">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Plan">
                <PlanSelect
                  value={form.subscription_plan}
                  onChange={(v) => setForm({ ...form, subscription_plan: v })}
                  testid="create-company-plan"
                />
              </Field>
              <Field label="Trial Days (basic only)">
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={form.subscription_plan === 'basic' ? form.trial_days : 0}
                  disabled={form.subscription_plan === 'pro'}
                  onChange={(e) => setForm({ ...form, trial_days: e.target.value })}
                  data-testid="create-company-trial-days"
                />
              </Field>
            </div>
            <p className="text-xs text-zinc-500 -mt-1">
              Basic = 1 user + unlimited invoices. Pro = unlimited users + invoices.
            </p>

            {/* Paid-plan period + expiry — optional at creation. Leave blank
                for a plain trial (`trial_ends_at` handles that automatically). */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-200">
              <Field label="Plan Period">
                <select
                  value={form.plan_period}
                  onChange={(e) => setForm({ ...form, plan_period: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid="create-company-plan-period"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </Field>
              <Field label="Plan Expires On">
                <Input
                  type="date"
                  value={form.plan_expires_at}
                  onChange={(e) => setForm({ ...form, plan_expires_at: e.target.value })}
                  placeholder="Optional — leave blank to skip"
                  data-testid="create-company-plan-expires"
                />
              </Field>
            </div>
            <p className="text-[11px] text-zinc-500 italic">
              Optional. Set when converting a tenant to a paid subscription — the app shows a warning ribbon 7 days before expiry.
            </p>
          </Section>

          <Section title="First Tenant Admin (optional)">
            <p className="text-xs text-zinc-500 -mt-2 mb-2">Leave blank to create the company without an admin — you can add users later via the tenant app.</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Admin Name">
                <Input
                  value={form.admin_name}
                  onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                  placeholder="Admin"
                  data-testid="create-company-admin-name"
                />
              </Field>
              <Field label="Admin Email">
                <Input
                  type="email"
                  value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  placeholder="admin@acme.com"
                  data-testid="create-company-admin-email"
                />
              </Field>
            </div>
            <Field label="Admin Password">
              <Input
                type="password"
                value={form.admin_password}
                onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                placeholder="Min 8 characters"
                data-testid="create-company-admin-password"
              />
            </Field>
          </Section>

          {err && <div className="text-sm text-red-500" data-testid="create-company-error">{err}</div>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white hover:opacity-90"
              data-testid="create-company-submit"
            >
              {loading ? 'Creating…' : 'Create Company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlanSelect({ value, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testid}>
      {['basic', 'pro'].map((p) => {
        const selected = value === p;
        return (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            data-testid={`${testid}-${p}`}
            className={`text-left px-3 py-2 rounded-md border text-sm transition-colors ${
              selected
                ? (p === 'pro'
                    ? 'border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700'
                    : 'border-indigo-400 bg-indigo-50 text-indigo-700')
                : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
            }`}
          >
            <div className="font-semibold uppercase tracking-wide text-[11px]">{p}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {p === 'basic' ? '1 user · 1,000 invoices' : 'Unlimited users & invoices'}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function EditCompanyDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', owner_email: '', phone: '',
    subscription_plan: 'basic', extend_trial_days: '',
    plan_period: 'monthly', plan_expires_at: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (target) {
      // Pre-fill the plan-expiry date input in YYYY-MM-DD format (the
      // HTML5 <input type="date"> expects local-date, not ISO datetime).
      const preExpiry = target.plan_expires_at
        ? new Date(target.plan_expires_at).toISOString().slice(0, 10)
        : '';
      setForm({
        name: target.name || '',
        owner_email: target.owner_email || '',
        phone: target.phone || '',
        subscription_plan: target.subscription_plan || 'basic',
        extend_trial_days: '',
        plan_period: target.plan_period || 'monthly',
        plan_expires_at: preExpiry,
      });
      setErr('');
    }
  }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true);
    setErr('');
    try {
      const payload = {
        name: form.name,
        owner_email: form.owner_email,
        phone: form.phone,
        subscription_plan: form.subscription_plan,
        // Paid-plan fields — send explicit null when the operator clears
        // the expiry so the backend $unset-style reset (via None) works.
        plan_period: form.plan_expires_at ? form.plan_period : null,
        plan_expires_at: form.plan_expires_at
          ? new Date(form.plan_expires_at + 'T23:59:59').toISOString()
          : null,
      };
      // Backend expects `plan_started_at` too — stamp now if setting fresh.
      if (form.plan_expires_at && !target.plan_started_at) {
        payload.plan_started_at = new Date().toISOString();
      }
      const days = parseInt(form.extend_trial_days, 10);
      if (!Number.isNaN(days) && days > 0) {
        payload.extend_trial_days = days;
      }
      await api.put(`/companies/${target.id}`, payload);
      toast.success('Company updated');
      onClose();
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const clearTrial = async () => {
    if (!target) return;
    setSaving(true);
    setErr('');
    try {
      await api.put(`/companies/${target.id}`, { trial_ends_at: '' });
      toast.success('Trial cleared');
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-company-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit {target?.name}</DialogTitle>
          <DialogDescription>Manage company metadata, plan and trial. Status is changed via the Power button.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-company-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner Email">
              <Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="edit-company-owner-email" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-company-phone" />
            </Field>
          </div>

          <div>
            <Label className="text-xs text-zinc-500 mb-1 block">Plan</Label>
            <PlanSelect
              value={form.subscription_plan}
              onChange={(v) => setForm({ ...form, subscription_plan: v })}
              testid="edit-company-plan"
            />
          </div>

          {target?.trial_ends_at && (
            <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700 flex items-center justify-between">
              <span>
                Trial ends <b>{new Date(target.trial_ends_at).toLocaleDateString()}</b>
                {' · '}
                {target.trial_expired
                  ? <b className="text-red-600">expired</b>
                  : <b>{target.trial_remaining_days} days left</b>}
              </span>
              <button
                type="button"
                onClick={clearTrial}
                disabled={saving}
                className="text-[11px] uppercase tracking-wide text-red-600 hover:underline"
                data-testid="edit-company-clear-trial"
              >
                Clear trial
              </button>
            </div>
          )}

          <Field label="Extend trial by (days)">
            <Input
              type="number"
              min="0"
              max="365"
              value={form.extend_trial_days}
              onChange={(e) => setForm({ ...form, extend_trial_days: e.target.value })}
              placeholder="e.g. 7"
              data-testid="edit-company-extend-trial"
            />
          </Field>

          {/* Paid-plan period + expiry — settable regardless of trial. Empty
              expiry clears both fields (SuperAdmin conversion back to trial). */}
          <div className="pt-3 border-t border-zinc-200">
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Paid Subscription</div>
            {target?.plan_expires_at && (
              <div className={`rounded-md border px-3 py-2 text-xs flex items-center justify-between mb-2 ${target.plan_expired ? 'border-red-300 bg-red-50 text-red-700' : (target.plan_remaining_days <= 7 ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-zinc-200 bg-zinc-50 text-zinc-700')}`}>
                <span>
                  Plan {target.plan_period || 'subscription'} ends <b>{new Date(target.plan_expires_at).toLocaleDateString()}</b>
                  {' · '}
                  {target.plan_expired
                    ? <b>expired</b>
                    : <b>{target.plan_remaining_days} day{target.plan_remaining_days === 1 ? '' : 's'} left</b>}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Plan Period">
                <select
                  value={form.plan_period}
                  onChange={(e) => setForm({ ...form, plan_period: e.target.value })}
                  className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
                  data-testid="edit-company-plan-period"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </Field>
              <Field label="Plan Expires On">
                <Input
                  type="date"
                  value={form.plan_expires_at}
                  onChange={(e) => setForm({ ...form, plan_expires_at: e.target.value })}
                  data-testid="edit-company-plan-expires"
                />
              </Field>
            </div>
            <p className="text-[11px] text-zinc-500 italic mt-1">
              Leave blank to remove the paid-plan expiry. Tenant sees an amber ribbon 7 days before expiry, red after.
            </p>
          </div>

          {err && <div className="text-sm text-red-500">{err}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="edit-company-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-zinc-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function EmailSettingsCard({ settings, onSaved }) {
  const [form, setForm] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 465,
    smtp_user: '',
    smtp_password: '',
    smtp_from_name: 'SMSuitePro',
    leads_notify_email: 'smsuitepro@gmail.com',
    auto_reply_enabled: false,
    auto_reply_subject: '',
    auto_reply_body: '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);

  // Hydrate from server whenever the parent settings change.
  useEffect(() => {
    if (!settings) return;
    setForm(f => ({
      ...f,
      smtp_host: settings.smtp_host || 'smtp.gmail.com',
      smtp_port: settings.smtp_port || 465,
      smtp_user: settings.smtp_user || '',
      smtp_password: '',
      smtp_from_name: settings.smtp_from_name || 'SMSuitePro',
      leads_notify_email: settings.leads_notify_email || 'smsuitepro@gmail.com',
      auto_reply_enabled: !!settings.auto_reply_enabled,
      auto_reply_subject: settings.auto_reply_subject || '',
      auto_reply_body: settings.auto_reply_body || '',
    }));
    setPasswordSet(!!settings.smtp_password_set);
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send the password if the operator typed a new one — blank
      // leaves the existing secret untouched server-side.
      if (!payload.smtp_password) delete payload.smtp_password;
      payload.smtp_port = Number(payload.smtp_port || 465);
      const { data } = await api.put('/platform-settings', payload);
      onSaved?.(data);
      setPasswordSet(!!data.smtp_password_set);
      setForm(f => ({ ...f, smtp_password: '' }));
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/marketing/test-email', {
        to: form.leads_notify_email,
      });
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  const canTest = passwordSet || form.smtp_password;

  return (
    <Card className="border-white/10 bg-[#141826] text-zinc-200" data-testid="email-settings-card">
      <CardContent className="p-5 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">Email Delivery</div>
            <div className="text-white text-lg font-semibold flex items-center gap-2">
              <EnvelopeSimple size={20} weight="duotone" className="text-fuchsia-300" />
              Lead Notification Emails
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Use a Gmail address + a 16-character App Password so lead & demo
              submissions email you the moment they land. Enable Auto-Reply to
              send visitors a friendly thank-you note automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!canTest || testing || !form.leads_notify_email}
              onClick={sendTest}
              className="border-white/20 text-zinc-200 hover:bg-white/10 bg-transparent"
              data-testid="email-settings-test-btn"
            >
              <PaperPlaneTilt size={14} className="mr-2" />
              {testing ? 'Sending…' : 'Send Test'}
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving}
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white h-9"
              data-testid="email-settings-save-btn"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">Gmail Address (SMTP User)</Label>
            <Input
              type="email"
              placeholder="you@gmail.com"
              value={form.smtp_user}
              onChange={e => setForm({ ...form, smtp_user: e.target.value })}
              className="bg-white/5 border-white/20 text-white h-9"
              data-testid="email-smtp-user"
              autoComplete="off"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">
              App Password (16 characters)
              {passwordSet && !form.smtp_password && (
                <span className="ml-2 text-emerald-300 text-[10px] uppercase tracking-wide">✓ set</span>
              )}
            </Label>
            <Input
              type="password"
              placeholder={passwordSet ? '•••• kept as-is' : 'xxxx xxxx xxxx xxxx'}
              value={form.smtp_password}
              onChange={e => setForm({ ...form, smtp_password: e.target.value })}
              className="bg-white/5 border-white/20 text-white h-9 font-mono"
              data-testid="email-smtp-password"
              autoComplete="new-password"
            />
            <p className="text-[10px] text-zinc-500">
              Generate at{' '}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
                className="text-fuchsia-300 hover:underline"
              >myaccount.google.com/apppasswords</a>
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">Notify Recipient</Label>
            <Input
              type="email"
              placeholder="smsuitepro@gmail.com"
              value={form.leads_notify_email}
              onChange={e => setForm({ ...form, leads_notify_email: e.target.value })}
              className="bg-white/5 border-white/20 text-white h-9"
              data-testid="email-notify-email"
            />
            <p className="text-[10px] text-zinc-500">Where every new lead email will be delivered.</p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">SMTP Host</Label>
            <Input
              value={form.smtp_host}
              onChange={e => setForm({ ...form, smtp_host: e.target.value })}
              className="bg-white/5 border-white/20 text-white h-9 font-mono text-xs"
              data-testid="email-smtp-host"
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">SMTP Port</Label>
            <Input
              type="number"
              min="1"
              max="65535"
              value={form.smtp_port}
              onChange={e => setForm({ ...form, smtp_port: e.target.value })}
              className="bg-white/5 border-white/20 text-white h-9 w-24"
              data-testid="email-smtp-port"
            />
            <p className="text-[10px] text-zinc-500">465 = SSL, 587 = STARTTLS</p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-zinc-400">From Display Name</Label>
            <Input
              value={form.smtp_from_name}
              onChange={e => setForm({ ...form, smtp_from_name: e.target.value })}
              className="bg-white/5 border-white/20 text-white h-9"
              data-testid="email-from-name"
            />
          </div>
        </div>

        {/* Auto-Reply */}
        <div className="border-t border-white/10 pt-5">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
            <div>
              <div className="text-white font-semibold flex items-center gap-2">
                Auto-Reply to Visitors
                {form.auto_reply_enabled && (
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px] uppercase">Enabled</Badge>
                )}
              </div>
              <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
                Send a thank-you email to the visitor the moment they submit a
                contact or demo form. Placeholders <code className="text-fuchsia-300">{'{name}'}</code>,
                {' '}<code className="text-fuchsia-300">{'{email}'}</code>,
                {' '}<code className="text-fuchsia-300">{'{business}'}</code> are replaced automatically.
              </p>
            </div>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_reply_enabled}
                onChange={e => setForm({ ...form, auto_reply_enabled: e.target.checked })}
                className="h-4 w-4 accent-fuchsia-500"
                data-testid="email-auto-reply-toggle"
              />
              <span className="text-sm text-zinc-300">Enable auto-reply</span>
            </label>
          </div>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Subject</Label>
              <Input
                value={form.auto_reply_subject}
                onChange={e => setForm({ ...form, auto_reply_subject: e.target.value })}
                className="bg-white/5 border-white/20 text-white h-9"
                data-testid="email-auto-reply-subject"
                disabled={!form.auto_reply_enabled}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">Body</Label>
              <textarea
                value={form.auto_reply_body}
                onChange={e => setForm({ ...form, auto_reply_body: e.target.value })}
                rows={6}
                disabled={!form.auto_reply_enabled}
                className="w-full rounded-md border border-white/20 bg-white/5 text-white px-3 py-2 text-sm font-mono disabled:opacity-50"
                data-testid="email-auto-reply-body"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

